import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { friendlyLoadErrorMessage } from '@/shared/lib/friendly-error'
import { toast } from '@/shared/lib/notify'

type ErrorToastOptions = {
  onRetry?: () => void
  retryLabel?: string
  /** 为 true 时不弹出 toast（例如已有预览数据兜底） */
  skip?: boolean
}

export function useErrorToast(
  error: string | null | undefined,
  options: ErrorToastOptions = {},
): void {
  const { t } = useTranslation()
  const onRetryRef = useRef(options.onRetry)
  onRetryRef.current = options.onRetry

  const retryLabel = options.retryLabel ?? t('common.retry', { defaultValue: '重试' })
  const friendlyError = useMemo(
    () => friendlyLoadErrorMessage(error, t),
    [error, t],
  )

  useEffect(() => {
    if (options.skip || !friendlyError) return

    const onRetry = onRetryRef.current
    toast.error(friendlyError, onRetry
      ? {
          action: {
            label: retryLabel,
            onClick: onRetry,
          },
        }
      : undefined)
  }, [friendlyError, options.skip, retryLabel])
}

export function useMergedErrorToast(
  errors: readonly (string | null | undefined)[],
  options: ErrorToastOptions = {},
): void {
  const merged = useMemo(() => {
    const unique = [...new Set(errors.filter(Boolean) as string[])]
    if (unique.length === 0) return null
    return unique.length === 1 ? unique[0]! : unique.join(' · ')
  }, [errors])

  useErrorToast(merged, options)
}
